import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowDown, Lock, ShieldCheck, Home as HomeIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { http } from "@/lib/api";
import { money, MONEY_PAGES } from "@/lib/constants";

function availabilityLabel(a) {
  if (!a) return null;
  if (a.status === "paused") return { text: "Temporarily paused", tone: "warn" };
  if (a.status === "waitlist") return { text: "At capacity — join waitlist", tone: "warn" };
  if (a.status === "available" && a.spots_left <= 3) return { text: `${a.spots_left} spots left`, tone: "ok" };
  return null;
}

const PRICE = { diagnostic: 99, blueprint: 279, guided: 749, inhome_overnight: 2549 };

export default function OfferLadder({ products = [] }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [clarityDone, setClarityDone] = useState(false);
  const byKey = Object.fromEntries(products.map((p) => [p.key, p]));

  useEffect(() => {
    if (user?.role === "customer") http.get("/portal/eligibility").then((r) => setClarityDone(r.data.clarity_completed)).catch(() => {});
  }, [user]);

  const priceOf = (k) => money(byKey[k]?.price ?? PRICE[k]);
  const buy = (k) => startCheckout({ product: k, user, navigate: nav });
  const avail = (k) => availabilityLabel(byKey[k]?.availability_status);
  const canBuy = (k) => (byKey[k]?.availability_status?.status ?? "available") === "available";

  return (
    <div data-testid="journey-section" className="space-y-5">
      {/* PRIMARY INTRO */}
      <div className="max-w-3xl">
        <h2 className="font-display text-3xl sm:text-4xl text-[#F7F5F0] tracking-tight">Your Infant Sleep Consultant Near Me Journey</h2>
        <p className="text-[#A39E98] mt-5 text-[17px] leading-relaxed">
          Every family working with <strong className="text-[#F7F5F0]">Infant Sleep Consultant Near Me</strong> starts with Clarity.
          Before a personalized infant sleep plan is created, a real <strong className="text-[#F7F5F0]">Certified Infant Sleep Consultant</strong> personally reviews what is happening, reads the information you provide, looks at your child's sleep patterns, and determines what should be addressed first.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 card-night rounded-full px-4 py-2" data-testid="trust-statement">
          <ShieldCheck size={16} className="text-[#C2836B]" />
          <span className="text-[#F7F5F0] text-sm font-medium">Human-reviewed. Human-written. Human-supported.</span>
        </div>
        <p className="text-[#738075] mt-5 text-sm leading-relaxed max-w-2xl">
          Infant Sleep Consultant Near Me is not an automated sleep-plan generator. Your Clarity Report, Personalized Sleep Blueprint, and any Guided Implementation support are prepared and reviewed by a real Certified Infant Sleep Consultant.
        </p>
      </div>

      {/* STAGE 1 — CLARITY */}
      <StageCard
        testid="stage-clarity" label="Stage 1 — Required First Step" tone="required"
        heading="Start With Clarity at Infant Sleep Consultant Near Me" price={priceOf("diagnostic")}
        body={<>Every family begins here. You complete a focused sleep intake through <strong className="text-[#F7F5F0]">Infant Sleep Consultant Near Me</strong>. A real <strong className="text-[#F7F5F0]">Certified Infant Sleep Consultant</strong> personally reads your answers, reviews the sleep patterns and concerns you describe, identifies the areas that matter most, and prepares your personalized Clarity Report.</>}
        note="Clarity must be completed before purchasing the standalone Personalized Sleep Blueprint."
        features={(byKey.diagnostic?.features || []).slice(0, 3)}
        cta={<PrimaryBtn testid="offer-buy-diagnostic" onClick={() => buy("diagnostic")}>{`Start With Clarity — ${priceOf("diagnostic")}`}</PrimaryBtn>}
        detail={() => nav(MONEY_PAGES.diagnostic)}
      />

      <Connector />

      {/* STAGE 2 — BLUEPRINT */}
      <StageCard
        testid="stage-blueprint" label="Stage 2 — Available After Clarity" tone="gold"
        heading="Your Personalized Infant Sleep Blueprint" price={priceOf("blueprint")}
        body={<>After your Clarity stage is complete, your <strong className="text-[#F7F5F0]">Certified Infant Sleep Consultant</strong> at Infant Sleep Consultant Near Me takes the next step with you. You provide the deeper information needed for planning, and your Certified Infant Sleep Consultant personally builds a complete sleep Blueprint around your child, daily routines, sleep patterns, family circumstances, goals, constraints, and implementation preferences.</>}
        features={(byKey.blueprint?.features || []).slice(0, 3)}
        emphasize="Once your Personalized Sleep Blueprint is complete, you have the full personalized plan you need to begin implementing it on your own."
        supporting="At this point, you are not required to purchase additional support. Your Infant Sleep Consultant Near Me Blueprint is designed to give you a clear, structured plan you can begin using at home."
        cta={clarityDone
          ? <PrimaryBtn testid="offer-buy-blueprint" outline onClick={() => buy("blueprint")}>{`Begin Your Blueprint — ${priceOf("blueprint")}`}</PrimaryBtn>
          : <div className="space-y-2">
              <div className="text-xs text-[#C49A5A] flex items-center gap-1"><Lock size={12} /> Available after Clarity</div>
              <PrimaryBtn testid="offer-buy-blueprint" outline onClick={() => buy("diagnostic")}>{`Start With Clarity — ${priceOf("diagnostic")}`}</PrimaryBtn>
            </div>}
        detail={() => nav(MONEY_PAGES.blueprint)}
      />

      {/* YOU NOW HAVE YOUR COMPLETE PLAN */}
      <div className="text-center py-2" data-testid="complete-plan-marker">
        <ArrowDown size={18} className="text-[#738075] mx-auto mb-3" />
        <div className="inline-block card-night rounded-xl px-6 py-4 border-[#C2836B]/40">
          <div className="eyebrow text-[#C2836B] mb-1">You now have your complete plan</div>
          <p className="text-[#F7F5F0] text-[15px] max-w-xl">With Clarity and your Personalized Sleep Blueprint complete, you hold the full personalized infant sleep plan — ready to put into practice.</p>
        </div>
      </div>

      {/* AFTER BLUEPRINT — TWO PATHS */}
      <div className="pt-2">
        <h3 className="font-display text-2xl text-[#F7F5F0] mb-1">What Happens After Your Infant Sleep Consultant Near Me Blueprint?</h3>
        <p className="text-[#738075] text-sm mb-5">Two clear paths — both entirely your choice.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="card-night rounded-xl p-6" data-testid="path-implement">
            <div className="eyebrow text-[#738075] mb-2">Path A</div>
            <h4 className="font-display text-xl text-[#F7F5F0]">Implement your Blueprint yourself</h4>
            <p className="text-[#A39E98] text-sm mt-3 leading-relaxed">Your Personalized Sleep Blueprint gives you the complete plan. If you feel comfortable putting the plan into practice independently, you can begin implementing it at home without purchasing additional support.</p>
          </div>
          <div className="card-night rounded-xl p-6 border-[#C2836B]/40" data-testid="path-guided">
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow text-[#C2836B]">Path B</div>
              <span className="text-[10px] uppercase tracking-wide bg-[#C49A5A]/15 text-[#d7ac63] rounded-full px-2 py-0.5">Optional after Blueprint</span>
            </div>
            <h4 className="font-display text-xl text-[#F7F5F0]">Want your Certified Infant Sleep Consultant beside you?</h4>
            <p className="text-[#C2836B] text-sm mt-1 font-medium">Standalone Guided Implementation Support — {priceOf("guided")}</p>
            <p className="text-[#A39E98] text-sm mt-3 leading-relaxed">If you completed Clarity and your Personalized Sleep Blueprint through Infant Sleep Consultant Near Me but later decide you want professional support during implementation, you can add Guided Implementation Support. Your Certified Infant Sleep Consultant stays involved while you put the Blueprint into practice and can help with:</p>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
              {["questions", "troubleshooting", "real-life challenges", "plan adjustments", "progress review", "implementation guidance"].map((f) => (
                <li key={f} className="flex gap-1.5 text-xs text-[#A39E98]"><Check size={13} className="text-[#738075] mt-0.5 shrink-0" />{f}</li>
              ))}
            </ul>
            <p className="text-[#738075] text-xs mt-4">This is <strong className="text-[#A39E98]">not required</strong> to use the Blueprint.</p>
          </div>
        </div>
      </div>

      {/* SEPARATE — COMPLETE GUIDED SLEEP JOURNEY ($749 UPFRONT) */}
      <div className="mt-8 rounded-2xl p-7 border border-[#C2836B]/45 bg-[#1C1B1A] relative overflow-hidden" data-testid="package-complete-guided">
        <span className="absolute top-5 right-5 text-[11px] uppercase tracking-wide font-semibold bg-[#C2836B] text-[#161514] rounded-full px-3 py-1">Full Journey — $749 Total</span>
        <div className="eyebrow text-[#C2836B] mb-2">Prefer the full journey from day one?</div>
        <h3 className="font-display text-2xl sm:text-3xl text-[#F7F5F0] max-w-2xl">Want Your Infant Sleep Consultant Near Me With You From the Beginning?</h3>
        <p className="text-[#A39E98] text-[15px] mt-1">Guided Virtual Consulting — <strong className="text-[#F7F5F0]">{priceOf("guided")} total upfront</strong></p>
        <p className="text-[#A39E98] text-sm mt-4 leading-relaxed max-w-2xl">For families who already know they want both a complete personalized sleep plan and ongoing support implementing it, <strong className="text-[#F7F5F0]">Infant Sleep Consultant Near Me</strong> offers the Guided Virtual Consulting.</p>
        <div className="flex flex-wrap items-center gap-2 my-4 text-sm">
          {["Clarity", "Personalized Sleep Blueprint", "Guided Implementation Support"].map((s, i) => (
            <span key={s} className="flex items-center gap-2">
              <span className="card-night rounded-full px-3 py-1.5 text-[#F7F5F0]">{s}</span>
              {i < 2 && <ArrowDown size={13} className="text-[#738075] -rotate-90" />}
            </span>
          ))}
        </div>
        <p className="text-[#D9D4CC] text-sm leading-relaxed max-w-2xl">
          <strong className="text-[#F7F5F0]">{priceOf("guided")} is the total upfront price</strong> for the Guided Virtual Consulting. Purchasing it upfront does not skip Clarity or Blueprint — your Certified Infant Sleep Consultant still personally guides the case through the full sequence: Clarity → Blueprint → Guided Implementation.
        </p>
        <p className="text-[#738075] text-sm mt-3 max-w-2xl">This option is for families who already know they want a Certified Infant Sleep Consultant involved not only in understanding and planning, but also while they implement the plan.</p>
        {avail("guided") && <div className={`text-xs mt-4 ${avail("guided").tone === "warn" ? "text-[#C49A5A]" : "text-[#8A998C]"}`}>{avail("guided").text}</div>}
        <div className="mt-5 flex flex-wrap gap-3">
          <PrimaryBtn testid="offer-buy-guided" onClick={() => canBuy("guided") ? buy("guided") : nav(MONEY_PAGES.guided)}>
            {canBuy("guided") ? `Start the Guided Virtual Consulting — ${priceOf("guided")}` : "Join Waitlist"}
          </PrimaryBtn>
          <button onClick={() => nav(MONEY_PAGES.guided)} className="text-xs text-[#738075] hover:text-[#A39E98] transition-colors self-center">See full details & boundaries</button>
        </div>
      </div>

      {/* STANDALONE PRICING NOTE (no combined total shown) */}
      <div className="card-night rounded-xl p-6" data-testid="pricing-note">
        <p className="text-[#A39E98] text-sm leading-relaxed max-w-2xl">Each service stands on its own. There are <strong className="text-[#F7F5F0]">no discounted upgrades and no credits</strong> — the Sleep Diagnostic does not apply toward the Personalized Sleep Blueprint. Start with Clarity and add only what you choose, when you choose it.</p>
      </div>

      {/* PREMIUM IN-HOME + OVERNIGHT */}
      <div className="rounded-2xl p-7 border border-[#2E2C2A] bg-[#161514]" data-testid="stage-inhome">
        <div className="flex items-center gap-2 mb-2"><HomeIcon size={15} className="text-[#C2836B]" /><div className="eyebrow text-[#C2836B]">Highest-touch support</div></div>
        <h3 className="font-display text-2xl sm:text-3xl text-[#F7F5F0] max-w-2xl">Premium In-Home + Overnight Infant Sleep Consultant Near Me Support</h3>
        <p className="text-[#A39E98] text-[15px] mt-1"><strong className="text-[#F7F5F0]">{priceOf("inhome_overnight")}</strong></p>
        <p className="text-[#A39E98] text-sm mt-4 leading-relaxed max-w-2xl">For Denver-area families seeking the highest level of hands-on support, <strong className="text-[#F7F5F0]">Infant Sleep Consultant Near Me</strong> offers Premium In-Home + Overnight support. A Certified Infant Sleep Consultant first reviews the family's situation and develops the appropriate personalized plan before hands-on implementation support begins.</p>
        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 max-w-2xl">
          {["Professional review", "Personalized planning", "In-home support", "Bedtime support", "Overnight guidance", "Real-time troubleshooting", "Morning debrief", "Follow-up"].map((f) => (
            <li key={f} className="flex gap-2 text-sm text-[#A39E98]"><Check size={15} className="text-[#738075] mt-0.5 shrink-0" />{f}</li>
          ))}
        </ul>
        <p className="text-[#738075] text-xs mt-4">This is not medical monitoring, nursing, or medical care.</p>
        {avail("inhome_overnight") && <div className={`text-xs mt-4 ${avail("inhome_overnight").tone === "warn" ? "text-[#C49A5A]" : "text-[#8A998C]"}`}>{avail("inhome_overnight").text}</div>}
        <div className="mt-5 flex flex-wrap gap-3">
          <PrimaryBtn testid="offer-buy-inhome_overnight" outline onClick={() => canBuy("inhome_overnight") ? buy("inhome_overnight") : nav(MONEY_PAGES.inhome_overnight)}>
            {canBuy("inhome_overnight") ? `Explore In-Home + Overnight — ${priceOf("inhome_overnight")}` : "Join Waitlist"}
          </PrimaryBtn>
          <button onClick={() => nav(MONEY_PAGES.inhome_overnight)} className="text-xs text-[#738075] hover:text-[#A39E98] transition-colors self-center">See full details & boundaries</button>
        </div>
      </div>
    </div>
  );
}

function StageCard({ testid, label, tone, heading, price, body, note, features = [], emphasize, supporting, cta, detail }) {
  const border = tone === "required" ? "border-[#C2836B]/50" : tone === "gold" ? "border-[#C49A5A]/40" : "";
  const labelTone = tone === "required" ? "text-[#C2836B]" : "text-[#C49A5A]";
  return (
    <div data-testid={testid} className={`card-night rounded-2xl p-7 ${border}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <span className={`eyebrow ${labelTone}`}>{label}</span>
        <span className="font-display text-3xl text-[#F7F5F0]">{price}</span>
      </div>
      <h3 className="font-display text-2xl text-[#F7F5F0] leading-snug">{heading}</h3>
      <p className="text-[#A39E98] text-sm mt-3 leading-relaxed max-w-2xl">{body}</p>
      {features.length > 0 && (
        <ul className="mt-4 grid sm:grid-cols-3 gap-x-5 gap-y-1.5">
          {features.map((f) => <li key={f} className="flex gap-2 text-xs text-[#A39E98]"><Check size={13} className="text-[#738075] mt-0.5 shrink-0" />{f}</li>)}
        </ul>
      )}
      {emphasize && (
        <div className="mt-4 rounded-lg bg-[#C2836B]/10 border border-[#C2836B]/30 px-4 py-3">
          <p className="text-[#F7F5F0] text-sm font-medium">{emphasize}</p>
        </div>
      )}
      {supporting && <p className="text-[#738075] text-sm mt-3 max-w-2xl leading-relaxed">{supporting}</p>}
      {note && <p className="text-[#C49A5A] text-xs mt-4">{note}</p>}
      <div className="mt-5">{cta}</div>
      <button onClick={detail} className="mt-2 text-xs text-[#738075] hover:text-[#A39E98] transition-colors">See full details & boundaries</button>
    </div>
  );
}

function Connector() {
  return <div className="flex justify-center py-1"><ArrowDown size={18} className="text-[#738075]" /></div>;
}

function PrimaryBtn({ children, onClick, outline, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`rounded-full px-6 py-3 text-sm ${outline ? "btn-outline-night" : "btn-clay"}`}>{children}</button>
  );
}
